// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — NodeDetailPanel: Floating card on node click
// White bg, borderRadius 8px, borderTop 2.5px navy, shadow
// Shows: Name, Type badge, Responsable, Description, Phase,
//        Linked documents (navigable), Linked tools
// ═══════════════════════════════════════════════════════════════

import { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import { FLOW_TOKENS as T } from '../shared/flowTokens';
import type {
  FlowNode,
  FlowNodeData,
  ActivityNodeData,
  DecisionNodeData,
  DocumentNodeData,
  SubprocessNodeData,
  ProcessRefNodeData,
  GovernanceNodeData,
} from '../types/flowchart.types';

interface NodeDetailPanelProps {
  node: FlowNode | null;
  onClose: () => void;
  onNavigateDocument?: (codigo: string) => void;
  onNavigateProcess?: (codigo: string) => void;
  initialPosition?: { x: number; y: number };
  procesoId?: string;
}

// ─── Node type labels ───

// NODE_TYPE_LABELS moved inline to use i18n

// ─── Section helper ───

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginBottom: 10,
        paddingBottom: 10,
        borderBottom: `1px solid ${T.color.border}`,
      }}
    >
      <div
        style={{
          fontSize: '9px',
          fontWeight: 600,
          fontFamily: T.font.family,
          color: T.color.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Value display helper ───

function ValueText({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: '11px',
        fontFamily: T.font.family,
        color: T.color.textPrimary,
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}

// ─── Component ───

export default function NodeDetailPanel({
  node,
  onClose,
  onNavigateDocument,
  onNavigateProcess,
  initialPosition,
  procesoId,
}: NodeDetailPanelProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const nodeTypeLabels: Record<string, string> = {
    ACTIVIDAD: t.flow.tipoActividad,
    DECISION: t.flow.tipoDecision,
    DOCUMENTO: t.flow.tipoDocumento,
    SUBPROCESO: t.flow.tipoSubproceso,
    INICIO: t.flow.tipoInicio,
    FIN: t.flow.tipoFin,
    PROCESO_REF: t.flow.tipoProcesoRefCorto,
    PROCESO_GOBIERNO: t.flow.tipoProcesoGobiernoCorto,
    RESULTADO: t.flow.tipoResultado,
  };
  // ─── Drag support ────────────────────────────────────────────────────────
  const [pos, setPos] = useState(initialPosition ?? { x: -1, y: -1 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (initialPosition) setPos(initialPosition);
  }, [initialPosition?.x, initialPosition?.y]);

  const onHeaderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragging.current = true;
      dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
      e.preventDefault();
    },
    [pos],
  );

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => {
      dragging.current = false;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const useFixedPos = pos.x >= 0 && pos.y >= 0;
  const handleDocClick = useCallback(
    (codigo: string) => onNavigateDocument?.(codigo),
    [onNavigateDocument],
  );

  const handleProcessClick = useCallback(
    (codigo: string) => onNavigateProcess?.(codigo),
    [onNavigateProcess],
  );

  // Click outside to close
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!node) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay to avoid closing immediately from the same click that opened it
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [node, onClose]);

  if (!node) return null;

  const data = node.data as FlowNodeData | undefined;
  if (!data) return null;

  return (
    <div
      ref={panelRef}
      style={{
        position: useFixedPos ? 'fixed' : 'absolute',
        ...(useFixedPos ? { left: pos.x, top: pos.y } : { top: 16, right: 16 }),
        width: 280,
        maxHeight: useFixedPos ? 'calc(100vh - 40px)' : 'calc(100% - 32px)',
        backgroundColor: T.color.surface,
        borderRadius: 8,
        borderTop: `2.5px solid ${T.color.navy}`,
        boxShadow: '0 4px 16px rgba(31,56,100,0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 40,
        overflow: 'hidden',
        fontFamily: T.font.family,
      }}
    >
      {/* Header: Name + Type badge + Close — draggable */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          padding: '12px 14px',
          borderBottom: `1px solid ${T.color.border}`,
          flexShrink: 0,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          {/* Name */}
          <div
            style={{
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: T.font.family,
              color: T.color.navy,
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
            }}
          >
            {data.label || t.flow.sinNombre}
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: T.color.textTertiary,
              flexShrink: 0,
            }}
            title={t.flow.cerrar}
          >
            <svg
              width={12}
              height={12}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Type badge */}
        {data.nodeType && (
          <span
            style={{
              display: 'inline-block',
              fontSize: '10px',
              fontWeight: 600,
              fontFamily: T.font.family,
              color: T.color.surface,
              backgroundColor: T.color.navy,
              padding: '2px 8px',
              borderRadius: 10,
              marginTop: 6,
              letterSpacing: '0.3px',
            }}
          >
            {nodeTypeLabels[data.nodeType] ?? data.nodeType}
          </span>
        )}

        {/* Responsable (inline in header for activity) */}
        {data.nodeType === 'ACTIVIDAD' && (data as ActivityNodeData).puestoNombre && (
          <div
            style={{
              fontSize: '11px',
              fontFamily: T.font.family,
              color: T.color.textSecondary,
              marginTop: 6,
            }}
          >
            <strong style={{ fontWeight: 600, color: T.color.textPrimary }}>
              {t.flow.responsable}:
            </strong>{' '}
            {(data as ActivityNodeData).puestoNombre}
          </div>
        )}

        {/* Description (inline in header) */}
        {data.description && (
          <div
            style={{
              fontSize: '11px',
              fontFamily: T.font.family,
              color: T.color.textSecondary,
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            {data.description}
          </div>
        )}

        {/* Phase badge */}
        {data.fase && (
          <div style={{ marginTop: 6 }}>
            <span
              style={{
                fontSize: '9px',
                fontWeight: 500,
                fontFamily: T.font.family,
                color: T.color.navy,
                backgroundColor: '#D6E4F0',
                padding: '2px 6px',
                borderRadius: 3,
              }}
            >
              {data.fase}
            </span>
          </div>
        )}
      </div>

      {/* Body — scrollable detail sections */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px 14px',
        }}
      >
        {/* Activity-specific: Linked documents */}
        {data.nodeType === 'ACTIVIDAD' &&
          ((data as ActivityNodeData).documentos ?? []).length > 0 && (
            <DetailSection title={t.flow.documentosVinculados}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {((data as ActivityNodeData).documentos ?? []).map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => handleDocClick(doc.codigo)}
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 6px',
                      borderRadius: 4,
                      border: 'none',
                      background: T.color.surfaceAlt,
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontFamily: T.font.family,
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#E8EDF4';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = T.color.surfaceAlt;
                    }}
                  >
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#818CF8"
                      strokeWidth={2}
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: T.color.navy,
                        fontSize: '10px',
                      }}
                    >
                      {doc.codigo}
                    </span>
                    <span
                      style={{
                        color: T.color.textSecondary,
                        fontSize: '10px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {doc.titulo}
                    </span>
                  </button>
                ))}
              </div>
            </DetailSection>
          )}

        {/* Activity-specific: Tools */}
        {data.nodeType === 'ACTIVIDAD' &&
          ((data as ActivityNodeData).herramientas ?? []).length > 0 && (
            <DetailSection title={t.flow.herramientas}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {((data as ActivityNodeData).herramientas ?? []).map((h, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: '9px',
                      fontWeight: 500,
                      fontFamily: T.font.family,
                      color: T.color.textSecondary,
                      backgroundColor: T.color.surfaceAlt,
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>
            </DetailSection>
          )}

        {/* Decision-specific */}
        {data.nodeType === 'DECISION' && (
          <>
            {(data as DecisionNodeData).condicion && (
              <DetailSection title={t.flow.condicion}>
                <ValueText>{(data as DecisionNodeData).condicion}</ValueText>
              </DetailSection>
            )}
            <DetailSection title={t.flow.etiquetasLabel}>
              <div style={{ display: 'flex', gap: 6 }}>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 500,
                    fontFamily: T.font.family,
                    color: '#16A34A',
                    backgroundColor: '#DCFCE7',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  Si: {(data as DecisionNodeData).etiquetaSi ?? 'Si'}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 500,
                    fontFamily: T.font.family,
                    color: '#DC2626',
                    backgroundColor: '#FEE2E2',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  No: {(data as DecisionNodeData).etiquetaNo ?? 'No'}
                </span>
              </div>
            </DetailSection>
          </>
        )}

        {/* Document-specific */}
        {data.nodeType === 'DOCUMENTO' && (data as DocumentNodeData).documentoCodigo && (
          <DetailSection title={t.flow.documentoReferenciado}>
            <button
              onClick={() => handleDocClick((data as DocumentNodeData).documentoCodigo ?? '')}
              style={{
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: T.color.navy,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              {(data as DocumentNodeData).documentoCodigo}
            </button>
          </DetailSection>
        )}

        {/* Subprocess-specific */}
        {data.nodeType === 'SUBPROCESO' && (
          <DetailSection title={t.flow.subprocesoLabel}>
            <ValueText>
              {t.flow.nodosInternos((data as SubprocessNodeData).childNodes?.length ?? 0)}
            </ValueText>
          </DetailSection>
        )}

        {/* Process reference */}
        {(data.nodeType === 'PROCESO_REF' || data.nodeType === 'PROCESO_GOBIERNO') && (
          <>
            {((data as ProcessRefNodeData).procesoCodigo ||
              (data as GovernanceNodeData).procesoCodigo) && (
              <DetailSection title={t.flow.procesoVinculado}>
                <button
                  onClick={() =>
                    handleProcessClick(
                      (data as ProcessRefNodeData).procesoCodigo ??
                        (data as GovernanceNodeData).procesoCodigo ??
                        '',
                    )
                  }
                  style={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: T.color.navy,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  {(data as ProcessRefNodeData).procesoCodigo ??
                    (data as GovernanceNodeData).procesoCodigo}
                </button>
              </DetailSection>
            )}
          </>
        )}

        {/* Nueva solicitud — solo actividades */}
        {data.nodeType === 'ACTIVIDAD' && procesoId && (
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: `1px solid ${T.color.border}` }}>
            <button
              onClick={() =>
                navigate(`/requests/nueva?procesoId=${procesoId}&actividadId=${node.id}`)
              }
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '7px 12px',
                borderRadius: 6,
                border: `1px dashed ${T.color.secondary}`,
                background: 'transparent',
                color: T.color.secondary,
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: T.font.family,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${T.color.secondary}10`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              {t.flow.nuevaSolicitud}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
